import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import {
  createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';

import { getAcknowledgmentEmployees } from '../../../api'

import styles from './styles.module.scss'

import Table from '../../Core/Table'
import { BackIcon } from '../Icons'

const columnHelper = createColumnHelper();

const formatDate = (date) => (date ? moment(date).format('MMM D, HH:mm') : '-')

const formatReadingTime = (seconds) => {
  if (seconds == null) return '-';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return minutes ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`
};

const AcknowledgmentEmployees = ({ documentId }) => {
  const { id: companyId } = useParams()
  const { t } = useTranslation()
  const history = useHistory()
  
  const [data, setData] = useState({
    document: {},
    stats: {},
    data: [],
  })
  const [loading, setLoading] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState('all')

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setLoading(true);
      const res = await getAcknowledgmentEmployees(companyId, documentId);
      if (active && res) {
        setData(res);
      }
      if (active) {
        setLoading(false);
      }
    };
    fetchData();
    return () => {
      active = false;
    };
  }, [companyId, documentId]);

  const filters = [
    { key: 'all', label: t('All'), count: data.stats.all || 0 },
    { key: 'not_seen', label: t('Not seen'), count: data.stats.not_seen || 0 },
    { key: 'seen', label: t('Seen'), count: data.stats.seen || 0 },
    { key: 'acknowledged', label: t('Acknowledged'), count: data.stats.acknowledged || 0 },
    { key: 'overdue', label: t('Overdue'), count: data.stats.overdue || 0 },
  ];

  const filteredData = useMemo(() => {
    if (selectedStatus === 'all') return data.data;
    if (selectedStatus === 'overdue') return data.data.filter((item) => item.overdue);
    return data.data.filter((item) => item.status === selectedStatus);
  }, [data.data, selectedStatus]);

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'employee',
      header: t('Employee'),
      enableSorting: false,
      cell: ({ row }) => {
        const employee = row.original.employee || {};
        const initials = `${(employee.name || '').charAt(0)}${(employee.surname || '').charAt(0)}`.toUpperCase();
        const employeeName = employee.full_name || `${employee.name || ''} ${employee.surname || ''}`.trim() || '-';
        return (
          <div className={styles.employeeCell}>
            {employee.avatar ? (
              <img className={styles.avatar} src={`${window.location.origin}/storage/${employee.avatar}`} alt='' />
            ) : (
              <div className={styles.avatar}>{initials}</div>
            )}
            <div className={styles.employeeInfo}>
              <div className={styles.employeeName}>{employeeName}</div>
              {employee.place?.name && <div className={styles.employeePlace}>{employee.place.name}</div>}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: t('Status'),
      enableSorting: false,
      cell: ({ getValue, row }) => {
        const status = getValue();
        let statusLabel = t('Not seen');
        if (status === 'acknowledged') statusLabel = t('Acknowledged');
        if (status === 'seen') statusLabel = t('Seen, not acknowledged');
        if (row.original.overdue) statusLabel = t('Overdue');
        const statusClass = row.original.overdue ? 'overdue' : status;
        return (
          <span className={`${styles.statusBadge} ${styles[`status_${statusClass}`] || ''}`}>
            <span className={styles.statusDot} />
            {statusLabel}
          </span>
        );
      },
    }),
    columnHelper.accessor((row) => row.first_seen_at, {
      id: 'first_seen_at',
      header: t('First seen'),
      enableSorting: false,
      cell: ({ getValue }) => <span className={styles.mutedText}>{formatDate(getValue())}</span>,
    }),
    columnHelper.accessor((row) => row.last_seen_at, {
      id: 'last_seen_at',
      header: t('Last seen'),
      enableSorting: false,
      cell: ({ getValue }) => <span className={styles.mutedText}>{formatDate(getValue())}</span>,
    }),
    columnHelper.accessor((row) => row.reading_time_seconds, {
      id: 'reading_time_seconds',
      header: t('Reading time'),
      cell: ({ getValue }) => <span>{formatReadingTime(getValue())}</span>,
    }),
    columnHelper.accessor((row) => row.acknowledged_at, {
      id: 'acknowledged_at',
      header: t('Acknowledged'),
      enableSorting: false,
      cell: ({ getValue }) => {
        const date = getValue();
        return <span className={date ? styles.acknowledgedDate : styles.mutedText}>{formatDate(date)}</span>;
      },
    }),
    columnHelper.accessor((row) => row.due_date, {
      id: 'due_date',
      header: t('Due date'),
      enableSorting: false,
      cell: ({ getValue }) => {
        const date = getValue();
        return <span className={styles.mutedText}>{date ? moment(date).format('MMM D, YYYY') : '-'}</span>;
      },
    }),
  ], [t]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getRowId: (row) => String(row.employee?.id),
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.backButton} onClick={history.goBack}>
          <BackIcon />
        </div>
        <div className={styles.headerInfo}>
          <div className={styles.headerTitle}>{data.document.name}</div>
          <div className={styles.headerDesc}>
            {data.document.document_type}
            {data.document.effective_date ? ` · effective  ${moment(data.document.effective_date).format('MMM D, YYYY')}` : ''}
            {data.document.due_date_for_action ? ` · due ${moment(data.document.due_date_for_action).format('MMM D, YYYY')}` : ''}
          </div>
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.filters} role='tablist' aria-label={t('Acknowledgment status')}>
          {filters.map((filter) => (
            <button
              key={filter.key}
              type='button'
              role='tab'
              aria-selected={selectedStatus === filter.key}
              className={`${styles.filter} ${selectedStatus === filter.key ? styles.filterActive : ''}`}
              onClick={() => setSelectedStatus(filter.key)}
            >
              {filter.label}
              {' '}
              <span>{filter.count}</span>
            </button>
          ))}
        </div>
        <Table
          table={table}
          columns={columns}
          loading={loading}
          placeholder={t('No employees found')}
          headerStyles={{ padding: '6px 10px' }}
        />
      </div>
    </div>
  );
};

export default AcknowledgmentEmployees;
